/**
 * Metadados de apresentação das notificações — funções e tabelas puras.
 *
 * A severidade e a categoria vêm do servidor (`severity`, `category`); aqui só se decide como se
 * mostram. A categoria é um rótulo discreto (ícone neutro) e a severidade é a única cor: o que é
 * crítico grita, o resto não compete.
 */

import {
  CalendarDays,
  ClipboardList,
  Factory,
  FileCheck2,
  GraduationCap,
  Info,
  Package,
  ShieldAlert,
  Stethoscope,
  Truck,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";

export type NotificationSeverity = "info" | "warning" | "critical";

export interface CategoryMeta {
  label: string;
  icon: LucideIcon;
}

const CATEGORY_META: Record<string, CategoryMeta> = {
  encomendas: { label: "Encomendas", icon: ClipboardList },
  fio: { label: "Fio e material", icon: Package },
  planeamento: { label: "Planeamento e teares", icon: Factory },
  qualidade: { label: "Qualidade", icon: ShieldAlert },
  subcontratacao: { label: "Subcontratação", icon: Truck },
  manutencao: { label: "Manutenção", icon: Wrench },
  certificacao: { label: "Certificação e dossier", icon: FileCheck2 },
  calendario: { label: "Calendário", icon: CalendarDays },
  rh: { label: "Recursos Humanos", icon: Users },
  formacao: { label: "Formação", icon: GraduationCap },
  saude: { label: "Saúde e segurança", icon: Stethoscope },
  sistema: { label: "Sistema", icon: Info },
};

export function categoryMeta(category: string | null | undefined): CategoryMeta {
  return CATEGORY_META[category ?? "sistema"] ?? CATEGORY_META.sistema;
}

export function normalizeSeverity(value: string | null | undefined): NotificationSeverity {
  return value === "critical" || value === "warning" ? value : "info";
}

export const SEVERITY_LABEL: Record<NotificationSeverity, string> = {
  critical: "Crítico",
  warning: "Atenção",
  info: "Informação",
};

/** Classes por severidade: a barra à esquerda da linha e a cor do ícone. */
export const SEVERITY_STYLE: Record<NotificationSeverity, { bar: string; icon: string; dot: string }> = {
  critical: { bar: "bg-destructive", icon: "text-destructive", dot: "bg-destructive" },
  warning: { bar: "bg-amber-500", icon: "text-amber-600 dark:text-amber-400", dot: "bg-amber-500" },
  info: { bar: "bg-transparent", icon: "text-muted-foreground", dot: "bg-primary" },
};

export interface NotificationLike {
  severity?: string | null;
  is_read: boolean;
  resolved_at?: string | null;
}

export type NotificationGroupKey = "attention" | "new" | "previous" | "resolved";

const GROUP_LABEL: Record<NotificationGroupKey, string> = {
  attention: "Requer atenção",
  new: "Novas",
  previous: "Anteriores",
  resolved: "Resolvidas",
};

/**
 * O grupo de uma notificação na lista: o que pede acção primeiro, depois o que é novo, depois o já lido.
 * Pura, para a lista do sino e a da página agruparem da mesma maneira.
 */
export function groupOf(n: NotificationLike): NotificationGroupKey {
  if (n.resolved_at) return "resolved";
  if (n.is_read) return "previous";
  return normalizeSeverity(n.severity) === "info" ? "new" : "attention";
}

export function groupNotifications<T extends NotificationLike>(items: T[]): { key: NotificationGroupKey; label: string; items: T[] }[] {
  const order: NotificationGroupKey[] = ["attention", "new", "previous", "resolved"];
  const buckets = new Map<NotificationGroupKey, T[]>();
  for (const n of items) {
    const key = groupOf(n);
    buckets.set(key, [...(buckets.get(key) ?? []), n]);
  }
  return order.filter((k) => buckets.has(k)).map((k) => ({ key: k, label: GROUP_LABEL[k], items: buckets.get(k)! }));
}
